import React, { useState, useEffect } from 'react';
import { 
  getUsers, resetUserPassword, updateUser, deleteUser, getLaboratories, createUser,
  getLabUsers, resetLabUserPassword, updateLabUser, deleteLabUser, createLabUser
} from '../api';
import Sidebar from './Sidebar';

const Users = ({ user, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [laboratories, setLaboratories] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [creatingUser, setCreatingUser] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    institution: '',
    role: 'member',
    laboratory: '',
    newPassword: ''
  });

  // Determine if user is superadmin or director
  const isSuperadmin = user.role === 'superadmin';
  const isDirector = user.role === 'director';

  const fetchUsers = async () => {
    try {
      const response = isDirector ? await getLabUsers() : await getUsers();
      setUsers(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch users');
      console.error('Error fetching users:', err);
    }
  };

  const fetchLaboratories = async () => {
    try {
      const response = await getLaboratories();
      setLaboratories(response.data);
    } catch (err) {
      console.error('Error fetching laboratories:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    if (isSuperadmin) {
      fetchLaboratories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin]);

  const handleEdit = (userToEdit) => {
    setEditingUser(userToEdit._id);
    setCreatingUser(false);
    setFormData({
      username: userToEdit.username,
      email: userToEdit.email,
      full_name: userToEdit.full_name || '',
      institution: userToEdit.institution || '',
      role: userToEdit.role || 'member',
      laboratory: userToEdit.laboratory ? userToEdit.laboratory._id : '',
      newPassword: ''
    });
  };

  const handleCreateNew = () => {
    setCreatingUser(true);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      full_name: '',
      institution: '',
      role: 'member',
      laboratory: '',
      newPassword: ''
    });
    setError('');
    setSuccess('');
  };

  const handleCreate = async () => {
    try {
      if (!formData.newPassword) {
        setError('Password is required for new users');
        return;
      }

      const createFunc = isDirector ? createLabUser : createUser;
      await createFunc({
        username: formData.username,
        email: formData.email,
        password: formData.newPassword,
        full_name: formData.full_name,
        institution: formData.institution,
        role: formData.role,
        laboratory: formData.laboratory || null
      });

      setSuccess('User created successfully');
      setCreatingUser(false);
      setFormData({ 
        username: '', 
        email: '', 
        full_name: '',
        institution: '',
        role: 'member', 
        laboratory: '',
        newPassword: '' 
      });
      await fetchUsers();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create user');
      console.error('Error creating user:', err);
    }
  };

  const handleUpdate = async (userId) => {
    try {
      const resetFunc = isDirector ? resetLabUserPassword : resetUserPassword;
      const updateFunc = isDirector ? updateLabUser : updateUser;

      if (formData.newPassword) {
        await resetFunc(userId, formData.newPassword);
      }
      
      await updateFunc(userId, {
        username: formData.username,
        email: formData.email,
        role: formData.role,
        laboratory: formData.laboratory || null
      });

      setSuccess('User updated successfully');
      setEditingUser(null);
      setFormData({ 
        username: '', 
        email: '', 
        full_name: '',
        institution: '',
        role: 'member', 
        laboratory: '',
        newPassword: '' 
      });
      await fetchUsers();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDelete = async (userId, username) => {
    if (window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      try {
        const deleteFunc = isDirector ? deleteLabUser : deleteUser;
        await deleteFunc(userId);
        setSuccess('User deleted successfully');
        await fetchUsers();
        
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      if (creatingUser) {
        await handleCreate();
      } else if (editingUser) {
        await handleUpdate(editingUser);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving user');
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="main-content">
        <div className="page-header">
          <h1>User Management</h1>
          <p>Manage all users in the platform</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            {success}
          </div>
        )}

        <div className="content-section">
          <div className="section-header">
            <h2>All Users</h2>
            <button 
              className="btn btn-success"
              onClick={handleCreateNew}
            >
              + New User
            </button>
          </div>

          {users.length === 0 ? (
            <div className="empty-state">
              <p>No users yet</p>
              <button className="btn btn-primary" onClick={handleCreateNew}>
                Add your first user
              </button>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Full Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    {isSuperadmin && <th>Laboratory</th>}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u._id}>
                      <td data-label="Username">{u.username}</td>
                      <td data-label="Full Name">{u.full_name || '-'}</td>
                      <td data-label="Email">{u.email}</td>
                      <td data-label="Role">
                        <span className={`status-badge status-${u.role}`}>
                          {u.role}
                        </span>
                      </td>
                      {isSuperadmin && (
                        <td data-label="Laboratory">
                          {u.laboratory?.name || '-'}
                        </td>
                      )}
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn btn-primary btn-small"
                            onClick={() => handleEdit(u)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-danger btn-small"
                            onClick={() => handleDelete(u._id, u.username)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Edit/Create User Modal */}
        {(editingUser || creatingUser) && (
          <div className="modal-overlay" onClick={() => { setEditingUser(null); setCreatingUser(false); }}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingUser ? 'Edit User' : 'Create New User'}</h2>
                <button className="close-btn" onClick={() => { setEditingUser(null); setCreatingUser(false); }}>×</button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Username *</label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="form-control"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="form-control"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Institution</label>
                    <input
                      type="text"
                      value={formData.institution}
                      onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Role</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="form-control"
                    >
                      <option value="member">Member</option>
                      <option value="director">Director</option>
                    </select>
                  </div>

                  {isSuperadmin && (
                    <div className="form-group">
                      <label>Assigned Laboratory</label>
                      <select
                        value={formData.laboratory}
                        onChange={(e) => setFormData({ ...formData, laboratory: e.target.value })}
                        className="form-control"
                      >
                        <option value="">-- No Laboratory --</option>
                        {laboratories.map(lab => (
                          <option key={lab._id} value={lab._id}>
                            {lab.name} ({lab.code})
                          </option>
                        ))}
                      </select>
                      <small className="form-text">
                        Users must be assigned to a laboratory to log in
                      </small>
                    </div>
                  )}

                  {isDirector && (
                    <div className="form-group">
                      <label>Laboratory</label>
                      <input
                        type="text"
                        value={user.laboratory?.name || 'No laboratory assigned'}
                        disabled
                        className="form-control"
                        style={{ backgroundColor: '#f5f5f5' }}
                      />
                      <small className="form-text">
                        New users will be automatically assigned to your laboratory
                      </small>
                    </div>
                  )}

                  <div className="form-group">
                    <label>{creatingUser ? 'Password *' : 'New Password (leave empty to keep current)'}</label>
                    <input
                      type="password"
                      value={formData.newPassword}
                      onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                      placeholder={creatingUser ? 'Enter password' : 'Enter new password'}
                      className="form-control"
                      required={creatingUser}
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={() => { setEditingUser(null); setCreatingUser(false); }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-success">
                    {creatingUser ? 'Create User' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;